/**
 * InvoiceBuilder — Squarespace-style full-page invoice editor.
 *
 *  Header bar:    [< Back]   "Create Invoice"            [📱/🖥 preview] [⋮] [SEND]
 *                              Draft / Sent / Voided
 *
 *  ┌──────────────────────────────────────┬───────────────────────────┐
 *  │ LEFT  — live preview (white card)    │ RIGHT — config (Content / │
 *  │   Site title, Invoice # / date,      │ Settings tabs). Flat rows │
 *  │   memo, big $amount + Due pill,      │ with chevron disclosure   │
 *  │   PAY INVOICE button, Billed-to,     │ and inline toggle switches│
 *  │   Order summary, totals.             │ matching Squarespace.     │
 *  └──────────────────────────────────────┴───────────────────────────┘
 *
 * Routes:
 *   /website-builder/invoicing/invoices/new        — fresh draft
 *   /website-builder/invoicing/invoices/:id/edit   — edit existing draft
 *                                                    (sent invoices open
 *                                                    in read-only review)
 *
 * Edit-mode supports:
 *   • Cancel (void) — for status in {sent, open}
 *   • Print / Save as PDF — uses print stylesheet or Stripe's hosted PDF
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, GripVertical, Copy, Trash2, Send, Smartphone, Monitor,
  ChevronRight, ChevronDown, ChevronUp, MoreHorizontal, Printer, XCircle,
  Search, UserPlus, X, Users, Calendar, Bell, Lock, FileText, Bold, Italic,
  List as ListIcon, Link as LinkIcon, Loader2,
} from 'lucide-react';
import NewClientModal from './NewClientModal';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const uid = () => Math.random().toString(36).slice(2, 10);
const cents = (v) => Math.round(Number(v || 0) * 100);
const dollars = (c) => ((c || 0) / 100).toFixed(2);
const money = (c) => `$${dollars(c)}`;

const emptyLineItem = () => ({ id: uid(), description: '', quantity: 1, unit_price: 0, discount_pct: 0, taxable: true });

const defaultState = () => ({
  number: '', title: 'Invoice',
  date_issued: new Date().toISOString().slice(0, 10),
  due_days: 0, // 0 = "Due upon receipt"
  po_number: '', project_name: '', contract_number: '', reference_number: '',
  custom_fields: [],
  client_id: null,
  client: { name: '', business_name: '', email: '', phone: '', billing_address: '', shipping_address: '', country: 'US', state: '', zip: '' },
  line_items: [emptyLineItem()],
  discount: { enabled: false, type: 'percent', value: 0 },
  tax_rate: 0,
  shipping_cents: 0,
  // Iter 170 — Two separate copy fields so users can independently control
  // the small header line under the date AND the longer footer memo.
  // `memo` (footer) remains for legacy compat; `header_message` is the new
  // explicit "message at the top under the date" field the user asked for.
  header_message: 'Thanks for your business!',
  memo: '',
  schedule_at: '',
  send_later: false,
  // Iter 170 — `end_date` lets users stop a recurring invoice on a specific
  // calendar date (alongside the legacy `count` cycles). Either/both can be
  // set; whichever fires first stops the series.
  recurring: { enabled: false, interval: 'month', count: 0, end_date: '' },
  reminders_enabled: true,
  // Standard reminder cadence (days past due → scheduled)
  reminder_offsets: [3, 7, 14, 30],
});

const REMINDER_LABEL = { 3: '3 Days overdue', 7: '7 Days overdue', 14: '14 Days overdue', 30: '30 Days overdue' };

const DUE_OPTIONS = [
  { v: 0,  label: 'Due upon receipt' },
  { v: 7,  label: 'Net 7' },
  { v: 14, label: 'Net 14' },
  { v: 30, label: 'Net 30' },
  { v: 60, label: 'Net 60' },
];

const fmtDateNice = (iso) => {
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return iso; }
};

const clientFromCRM = (c) => ({
  name: c.name || '', business_name: c.business_name || '',
  email: c.email || '', phone: c.phone || '',
  billing_address: c.billing_address || '', shipping_address: c.shipping_address || '',
  country: c.country || 'US', state: c.state || '', zip: c.zip || '',
});

const STATUS_PILLS = {
  draft:   { label: 'Draft',   color: 'text-white/50' },
  sent:    { label: 'Sent',    color: 'text-cyan-300' },
  open:    { label: 'Sent',    color: 'text-cyan-300' },
  paid:    { label: 'Paid',    color: 'text-emerald-300' },
  overdue: { label: 'Overdue', color: 'text-rose-300' },
  void:    { label: 'Voided',  color: 'text-white/40 line-through' },
  voided:  { label: 'Voided',  color: 'text-white/40 line-through' },
};

export default function InvoiceBuilder() {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const [params] = useSearchParams();
  const [state, setState] = useState(defaultState());
  const [invoiceId, setInvoiceId] = useState(routeId || null);
  const [invoiceStatus, setInvoiceStatus] = useState('draft');
  const [stripePdfUrl, setStripePdfUrl] = useState(null);
  const [stripeHostedUrl, setStripeHostedUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [previewMode, setPreviewMode] = useState('desktop'); // desktop | mobile
  const [activeTab, setActiveTab] = useState('content');     // content | settings
  const [openSection, setOpenSection] = useState('client');  // current expanded row group
  const [showNewClient, setShowNewClient] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const isReadOnly = invoiceStatus !== 'draft';

  // Iter 170 — Pull the user's brand logo + name from /api/settings so the
  // invoice preview/print can show their business identity at the top
  // automatically. Falls back to a generic title when nothing is set.
  const [brand, setBrand] = useState({ logo_url: '', brand_name: '' });
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/settings`, { headers: auth() });
        if (!live) return;
        setBrand({
          logo_url: data?.logo_url || '',
          brand_name: data?.brand_name || '',
        });
      } catch { /* unauthenticated / missing — fall back to defaults */ }
    })();
    return () => { live = false; };
  }, []);

  /* ─────────────── Load existing invoice (edit mode) ─────────────── */
  useEffect(() => {
    if (!routeId) return;
    let active = true;
    (async () => {
      setBusy(true);
      try {
        const { data } = await axios.get(`${API}/builder/invoices/${routeId}`, { headers: auth() });
        if (!active) return;
        const m = data.meta || {};
        setInvoiceId(data.id);
        setInvoiceStatus(data.status || 'draft');
        setStripePdfUrl(data.stripe_pdf_url || null);
        setStripeHostedUrl(data.stripe_invoice_url || null);
        setState(s => ({
          ...defaultState(),
          ...s,
          number: m.number || '',
          title:  m.title || s.title,
          date_issued: m.date_issued || s.date_issued,
          due_days: typeof data.due_days === 'number' ? data.due_days : s.due_days,
          po_number: m.po_number || '', project_name: m.project_name || '',
          contract_number: m.contract_number || '', reference_number: m.reference_number || '',
          custom_fields: m.custom_fields || [],
          client_id: data.client_id || null,
          client: m.client || {
            name: data.customer_name || '', business_name: '', email: data.customer_email || '',
            phone: '', billing_address: '', shipping_address: '', country: 'US', state: '', zip: '',
          },
          line_items: (data.line_items || []).map(li => ({
            id: uid(),
            description: li.description,
            quantity: li.quantity,
            unit_price: (li.unit_amount || 0) / 100,
            discount_pct: 0,
            taxable: true,
          })),
          discount: m.discount || { enabled: false, type: 'percent', value: 0 },
          tax_rate: m.tax_rate || 0,
          shipping_cents: m.shipping_cents || 0,
          memo: m.memo || '',
          header_message: m.header_message || data.header_message || s.header_message,
          schedule_at: m.schedule_at || '',
          send_later:  !!m.schedule_at,
          recurring:   m.recurring || s.recurring,
          reminders_enabled: m.reminders_enabled !== false,
          reminder_offsets:  m.reminder_offsets || [3, 7, 14, 30],
        }));
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Could not load invoice');
        navigate('/website-builder/invoicing');
      } finally { setBusy(false); }
    })();
    return () => { active = false; };
  }, [routeId, navigate]);

  /* ─────────────── Prefill from ?client=<id> ─────────────── */
  useEffect(() => {
    const cid = params.get('client');
    if (!cid || routeId) return; // not on edit-mode
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/builder/crm/clients/${cid}`, { headers: auth() });
        if (active) setState(s => ({ ...s, client_id: data.id, client: clientFromCRM(data) }));
      } catch (e) { /* silent */ }
    })();
    return () => { active = false; };
  }, [params, routeId]);

  /* ─────────────── helpers ─────────────── */
  const set = (path, value) => setState(s => {
    const next = JSON.parse(JSON.stringify(s));
    const parts = path.split('.');
    let cur = next;
    for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
    cur[parts[parts.length - 1]] = value;
    return next;
  });

  const addLine = () => setState(s => ({ ...s, line_items: [...s.line_items, emptyLineItem()] }));
  const dupLine = (i) => setState(s => { const next = [...s.line_items]; next.splice(i + 1, 0, { ...next[i], id: uid() }); return { ...s, line_items: next }; });
  const delLine = (i) => setState(s => ({ ...s, line_items: s.line_items.filter((_, j) => j !== i) }));
  const updLine = (i, k, v) => setState(s => { const next = [...s.line_items]; next[i] = { ...next[i], [k]: v }; return { ...s, line_items: next }; });
  const moveLine = (from, to) => setState(s => { const next = [...s.line_items]; const [m] = next.splice(from, 1); next.splice(to, 0, m); return { ...s, line_items: next }; });
  const [dragIdx, setDragIdx] = useState(null);

  const addCF = () => setState(s => ({ ...s, custom_fields: [...s.custom_fields, { id: uid(), label: '', value: '' }] }));
  const updCF = (id, k, v) => setState(s => ({ ...s, custom_fields: s.custom_fields.map(f => f.id === id ? { ...f, [k]: v } : f) }));
  const delCF = (id) => setState(s => ({ ...s, custom_fields: s.custom_fields.filter(f => f.id !== id) }));

  /* ─────────────── totals ─────────────── */
  const totals = useMemo(() => {
    const subtotal = state.line_items.reduce((sum, li) => {
      const gross = (Number(li.quantity) || 0) * cents(li.unit_price);
      const lineDiscount = gross * (Number(li.discount_pct) || 0) / 100;
      return sum + Math.round(gross - lineDiscount);
    }, 0);
    const invDiscount = state.discount.enabled
      ? (state.discount.type === 'percent'
          ? Math.round(subtotal * (Number(state.discount.value) || 0) / 100)
          : cents(state.discount.value))
      : 0;
    const taxableBase = Math.max(0, subtotal - invDiscount);
    const tax = Math.max(0, Math.round(taxableBase * (Number(state.tax_rate) || 0) / 100));
    const shipping = Math.max(0, Math.round(Number(state.shipping_cents) || 0));
    const total = Math.max(0, subtotal - invDiscount + tax + shipping);
    return { subtotal, invDiscount, tax, shipping, total };
  }, [state]);

  /* ─────────────── save ─────────────── */
  const buildPayload = () => {
    // Iter 170 — When both Name and Business are filled, combine them so
    // the legacy `customer_name` column reads "<Name> · <Business>" rather
    // than dropping one. The structured client object in `meta.client`
    // still keeps them as separate fields.
    const personName = (state.client.name || '').trim();
    const bizName = (state.client.business_name || '').trim();
    const combinedName = personName && bizName
      ? `${personName} · ${bizName}`
      : (personName || bizName);
    return ({
    customer_name:  combinedName,
    customer_email: state.client.email,
    client_id:      state.client_id,
    currency: 'usd',
    due_days: Number(state.due_days) || 0,
    notes: state.memo || '',
    line_items: state.line_items
      .filter(li => li.description && Number(li.unit_price) > 0)
      .map(li => ({
        description: li.description,
        quantity:    Number(li.quantity) || 1,
        // Backend expects DOLLARS (float); it converts to cents via _to_cents.
        unit_amount: Number((Number(li.unit_price) || 0) * (1 - (Number(li.discount_pct) || 0) / 100)).toFixed(2),
      })),
    meta: {
      number: state.number, title: state.title, date_issued: state.date_issued,
      po_number: state.po_number, project_name: state.project_name,
      contract_number: state.contract_number, reference_number: state.reference_number,
      custom_fields: state.custom_fields,
      client: state.client,
      discount: state.discount, tax_rate: state.tax_rate, shipping_cents: state.shipping_cents,
      schedule_at: state.send_later ? state.schedule_at : '',
      recurring: state.recurring,
      header_message: state.header_message || '',
      memo: state.memo || '',
      reminders_enabled: state.reminders_enabled,
      reminder_offsets:  state.reminder_offsets,
    },
  });
  };

  const validate = () => {
    if (!state.client.email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(state.client.email)) {
      toast.error('Add a valid client email'); return false;
    }
    if (state.line_items.filter(li => li.description && Number(li.unit_price) > 0).length === 0) {
      toast.error('Add at least one line item with a price'); return false;
    }
    return true;
  };

  const saveDraft = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const payload = buildPayload();
      let res;
      if (invoiceId) {
        res = await axios.put(`${API}/builder/invoices/${invoiceId}`, payload, { headers: auth() });
      } else {
        res = await axios.post(`${API}/builder/invoices`, payload, { headers: auth() });
      }
      setInvoiceId(res.data.id);
      toast.success('Draft saved');
      return res.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save'); return null;
    } finally { setBusy(false); }
  };

  const send = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const saved = await (async () => {
        if (invoiceId) {
          const r = await axios.put(`${API}/builder/invoices/${invoiceId}`, buildPayload(), { headers: auth() });
          return r.data;
        }
        const r = await axios.post(`${API}/builder/invoices`, buildPayload(), { headers: auth() });
        return r.data;
      })();
      const sent = await axios.post(`${API}/builder/invoices/${saved.id}/send`, {}, { headers: auth() });
      setInvoiceId(saved.id);
      setInvoiceStatus(sent.data.status || 'sent');
      setStripePdfUrl(sent.data.stripe_pdf_url || null);
      setStripeHostedUrl(sent.data.stripe_invoice_url || null);
      toast.success('Invoice sent!');
      navigate('/website-builder/invoicing');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send'); setBusy(false);
    }
  };

  const cancelInvoice = async () => {
    if (!invoiceId) return;
    if (!(await themedConfirm('Cancel and void this invoice? The client will no longer be able to pay it. This cannot be undone.'))) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/builder/invoices/${invoiceId}/void`, {}, { headers: auth() });
      setInvoiceStatus(data.status || 'void');
      toast.success('Invoice voided');
      setShowMenu(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not void');
    } finally { setBusy(false); }
  };

  const printInvoice = () => {
    // If Stripe gave us its rendered PDF, prefer that for a perfect document.
    if (stripePdfUrl) {
      window.open(stripePdfUrl, '_blank', 'noopener'); setShowMenu(false); return;
    }
    setShowMenu(false);
    setTimeout(() => window.print(), 100); // let menu close before print dialog opens
  };

  const statusPill = STATUS_PILLS[invoiceStatus] || STATUS_PILLS.draft;
  const canCancel = ['sent', 'open', 'overdue'].includes(invoiceStatus);

  /* ─────────────── render ─────────────── */
  return (
    <div className="min-h-screen bg-[#0a0a1f] text-white" data-testid="invoice-builder">
      <PrintStyles />

      {/* ──── HEADER ──── */}
      <header className="border-b border-white/5 bg-[#0a0a1f]/95 backdrop-blur sticky top-0 z-30 no-print">
        <div className="max-w-[1700px] mx-auto px-5 py-3 grid grid-cols-3 items-center gap-4">
          <div>
            <Link to="/website-builder/invoicing" className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-sm" data-testid="builder-back">
              <ArrowLeft className="w-4 h-4" /> Back
            </Link>
          </div>
          <div className="text-center">
            <div className="text-sm font-semibold tracking-wide">{invoiceId ? 'Edit Invoice' : 'Create Invoice'}</div>
            <div className={`text-[11px] uppercase tracking-[0.18em] ${statusPill.color}`}>{statusPill.label}</div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <div className="flex rounded-lg bg-white/5 p-0.5 border border-white/10" data-testid="preview-mode-toggle">
              <button onClick={() => setPreviewMode('desktop')} data-testid="preview-desktop"
                className={`p-1.5 rounded-md ${previewMode === 'desktop' ? 'bg-white/15 text-white' : 'text-white/40 hover:text-white'}`}>
                <Monitor className="w-4 h-4" />
              </button>
              <button onClick={() => setPreviewMode('mobile')} data-testid="preview-mobile"
                className={`p-1.5 rounded-md ${previewMode === 'mobile' ? 'bg-white/15 text-white' : 'text-white/40 hover:text-white'}`}>
                <Smartphone className="w-4 h-4" />
              </button>
            </div>
            <div className="relative">
              <button onClick={() => setShowMenu(s => !s)} data-testid="menu-btn"
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/80">
                <MoreHorizontal className="w-4 h-4" />
              </button>
              {showMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
                  <div className="absolute right-0 mt-1 w-56 rounded-xl bg-[#11112a] border border-white/10 shadow-2xl z-50 overflow-hidden" data-testid="menu-dropdown">
                    {invoiceId && !isReadOnly && (
                      <button onClick={() => { setShowMenu(false); saveDraft(); }} data-testid="menu-save-draft"
                        className="w-full text-left px-4 py-2.5 text-sm text-white/80 hover:bg-white/5 inline-flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5" /> Save draft
                      </button>
                    )}
                    {!invoiceId && (
                      <button onClick={() => { setShowMenu(false); saveDraft(); }} data-testid="menu-save-draft"
                        className="w-full text-left px-4 py-2.5 text-sm text-white/80 hover:bg-white/5 inline-flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5" /> Save as draft
                      </button>
                    )}
                    <button onClick={printInvoice} data-testid="menu-print"
                      className="w-full text-left px-4 py-2.5 text-sm text-white/80 hover:bg-white/5 inline-flex items-center gap-2">
                      <Printer className="w-3.5 h-3.5" /> Print / Save as PDF
                    </button>
                    {stripeHostedUrl && (
                      <a href={stripeHostedUrl} target="_blank" rel="noreferrer" onClick={() => setShowMenu(false)} data-testid="menu-hosted"
                        className="w-full text-left px-4 py-2.5 text-sm text-white/80 hover:bg-white/5 inline-flex items-center gap-2">
                        <LinkIcon className="w-3.5 h-3.5" /> Open client view
                      </a>
                    )}
                    {canCancel && (
                      <button onClick={cancelInvoice} data-testid="menu-cancel"
                        className="w-full text-left px-4 py-2.5 text-sm text-rose-300 hover:bg-rose-500/10 inline-flex items-center gap-2 border-t border-white/5">
                        <XCircle className="w-3.5 h-3.5" /> Cancel invoice
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
            <button onClick={send} disabled={busy || isReadOnly} data-testid="send-invoice"
              className="px-5 py-2 rounded-lg bg-black text-white text-sm font-semibold tracking-wider uppercase inline-flex items-center gap-2 disabled:opacity-40 border border-white/15 hover:bg-zinc-900">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              <span>SEND</span>
              <Lock className="w-3 h-3 opacity-50" />
            </button>
          </div>
        </div>
      </header>

      {/* ──── BODY ──── */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-0 min-h-[calc(100vh-60px)]">
        {/* LEFT — preview */}
        <div className="bg-gradient-to-b from-[#0a0a1f] to-[#0e0e2a] py-10 px-6 print-only-area" data-testid="invoice-preview-pane">
          <div className={`mx-auto transition-all duration-300 ${previewMode === 'mobile' ? 'max-w-[420px]' : 'max-w-[640px]'}`}>
            <SquarespacePreview state={state} totals={totals} status={invoiceStatus} brand={brand} />
          </div>
        </div>

        {/* RIGHT — config panel (Squarespace style) */}
        <aside className="bg-[#11112a] border-l border-white/5 lg:sticky lg:top-[60px] lg:self-start lg:h-[calc(100vh-60px)] overflow-y-auto no-print" data-testid="config-panel">
          {/* Tabs */}
          <div className="px-5 pt-5 pb-3 border-b border-white/5 sticky top-0 bg-[#11112a] z-10">
            <div className="flex gap-6 text-sm">
              <Tab id="content"  active={activeTab} setActive={setActiveTab}>Content</Tab>
              <Tab id="settings" active={activeTab} setActive={setActiveTab}>Settings</Tab>
            </div>
          </div>

          {isReadOnly && (
            <div className="mx-5 mt-4 px-3 py-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-200 text-xs inline-flex items-center gap-2" data-testid="readonly-banner">
              <Lock className="w-3 h-3" /> {statusPill.label} invoices are read-only. Use the menu to print or cancel.
            </div>
          )}

          {activeTab === 'content' ? (
            <div className="divide-y divide-white/5">
              {/* CLIENT DETAILS */}
              <SectionGroup title="Client details" testid="grp-client">
                {state.client_id ? (
                  <PickedClient state={state} onClear={() => setState(s => ({ ...s, client_id: null }))} />
                ) : (
                  <DisclosureRow
                    id="client" open={openSection === 'client'} setOpen={setOpenSection}
                    label={state.client.email
                      ? ([state.client.name, state.client.business_name].filter(Boolean).join(' · ') || state.client.email)
                      : 'Add or find a client'}
                    valueHint={state.client.email && (state.client.business_name || state.client.name) ? state.client.email : ''}
                    testid="row-client"
                  >
                    <ClientPicker state={state} setState={setState} onShowNewClient={() => setShowNewClient(true)} />
                    <ClientForm state={state} set={set} />
                  </DisclosureRow>
                )}
              </SectionGroup>

              {/* INVOICE DETAILS */}
              <SectionGroup title="Invoice details" testid="grp-invoice">
                <DisclosureRow id="name" open={openSection === 'name'} setOpen={setOpenSection} label="Name" valueHint={state.title || 'Invoice'} testid="row-name">
                  <input value={state.title} onChange={e => set('title', e.target.value)} data-testid="invoice-title-input" disabled={isReadOnly}
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-violet-400" />
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <FieldStacked label="Invoice number" v={state.number} on={v => set('number', v)} testid="invoice-number-input" placeholder="auto" disabled={isReadOnly} />
                    <FieldStacked label="Date issued" type="date" v={state.date_issued} on={v => set('date_issued', v)} testid="invoice-date-input" disabled={isReadOnly} />
                  </div>
                </DisclosureRow>

                <DisclosureRow id="custom" open={openSection === 'custom'} setOpen={setOpenSection} label="Custom fields"
                  valueHint={`${state.custom_fields.length} active`} testid="row-custom">
                  {state.custom_fields.map(f => (
                    <div key={f.id} className="flex gap-2 mb-2" data-testid={`cf-row-${f.id}`}>
                      <input value={f.label} onChange={e => updCF(f.id, 'label', e.target.value)} placeholder="Label" disabled={isReadOnly}
                        className="flex-1 px-2.5 py-1.5 rounded bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-violet-400" />
                      <input value={f.value} onChange={e => updCF(f.id, 'value', e.target.value)} placeholder="Value" disabled={isReadOnly}
                        className="flex-1 px-2.5 py-1.5 rounded bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-violet-400" />
                      <button onClick={() => delCF(f.id)} disabled={isReadOnly} className="px-1.5 text-rose-300/70 hover:text-rose-200 disabled:opacity-30"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                  <button onClick={addCF} disabled={isReadOnly} data-testid="add-custom-field" className="text-violet-300 hover:text-violet-200 text-xs inline-flex items-center gap-1.5 disabled:opacity-30">
                    <Plus className="w-3 h-3" /> Add custom field
                  </button>
                </DisclosureRow>

                <DisclosureRow id="header_msg" open={openSection === 'header_msg'} setOpen={setOpenSection}
                  label="Header message"
                  valueHint={truncateHtml(state.header_message, 28) || 'Top of invoice, under date'}
                  testid="row-header-msg">
                  <p className="text-[11px] text-white/40 mb-2">Short note shown at the top of the invoice, just under the date.</p>
                  <MemoEditor value={state.header_message} onChange={v => set('header_message', v)} disabled={isReadOnly} />
                </DisclosureRow>

                <DisclosureRow id="memo" open={openSection === 'memo'} setOpen={setOpenSection}
                  label="Footer note"
                  valueHint={truncateHtml(state.memo, 28) || 'Optional bottom note'}
                  testid="row-memo">
                  <p className="text-[11px] text-white/40 mb-2">Longer note that prints at the bottom of the invoice (terms, thank-you, etc.).</p>
                  <MemoEditor value={state.memo} onChange={v => set('memo', v)} disabled={isReadOnly} />
                </DisclosureRow>
              </SectionGroup>

              {/* ORDER SUMMARY */}
              <SectionGroup title="Order summary" testid="grp-items">
                <div className="px-5 py-2 space-y-2">
                  {state.line_items.map((li, i) => (
                    <LineItemRow key={li.id} idx={i} li={li}
                      onUpdate={(k, v) => updLine(i, k, v)} onDup={() => dupLine(i)} onDel={() => delLine(i)}
                      onDragStart={() => setDragIdx(i)} onDragOver={(e) => e.preventDefault()}
                      onDrop={() => { if (dragIdx !== null && dragIdx !== i) moveLine(dragIdx, i); setDragIdx(null); }}
                      dragging={dragIdx === i} disabled={isReadOnly}
                    />
                  ))}
                </div>
                <div className="px-5 pb-3">
                  <button onClick={addLine} disabled={isReadOnly} data-testid="add-line"
                    className="w-full px-4 py-3 rounded-lg border border-white/10 hover:border-violet-400/40 hover:bg-white/5 text-white text-xs font-semibold tracking-[0.18em] uppercase inline-flex items-center justify-center gap-2 disabled:opacity-30">
                    <Plus className="w-3.5 h-3.5" /> Add line item
                  </button>
                </div>
              </SectionGroup>
            </div>
          ) : (
            /* SETTINGS TAB */
            <div className="divide-y divide-white/5">
              {/* DISCOUNT */}
              <SectionGroup title="Discount" testid="grp-discount">
                <ToggleRow label="Apply a discount" checked={state.discount.enabled} onChange={v => set('discount.enabled', v)} testid="toggle-discount" disabled={isReadOnly} />
                {state.discount.enabled && (
                  <div className="px-5 pb-3 grid grid-cols-2 gap-2">
                    <select value={state.discount.type} onChange={e => set('discount.type', e.target.value)} data-testid="discount-type" disabled={isReadOnly}
                      className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-violet-400">
                      <option value="percent">Percent (%)</option>
                      <option value="fixed">Fixed ($)</option>
                    </select>
                    <input type="number" min={0} step="0.01" value={state.discount.value} onChange={e => set('discount.value', e.target.value)} data-testid="discount-value" disabled={isReadOnly}
                      className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-violet-400" />
                  </div>
                )}
              </SectionGroup>

              {/* TAX + SHIPPING */}
              <SectionGroup title="Tax & Shipping" testid="grp-taxship">
                <div className="px-5 py-3 grid grid-cols-2 gap-2">
                  <FieldStacked label="Tax rate (%)" type="number" v={state.tax_rate} on={v => set('tax_rate', v)} testid="tax-rate" disabled={isReadOnly} />
                  <FieldStacked label="Shipping ($)" type="number" v={state.shipping_cents / 100} on={v => set('shipping_cents', Math.round(Number(v || 0) * 100))} testid="shipping" disabled={isReadOnly} />
                </div>
              </SectionGroup>

              {/* PLANNING */}
              <SectionGroup title="Planning" testid="grp-planning">
                <ToggleRow label="Send later" checked={state.send_later} onChange={v => set('send_later', v)} testid="toggle-send-later" disabled={isReadOnly} />
                {state.send_later && (
                  <div className="px-5 pb-2">
                    <input type="datetime-local" value={state.schedule_at} onChange={e => set('schedule_at', e.target.value)} data-testid="schedule-at" disabled={isReadOnly}
                      className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-violet-400" />
                  </div>
                )}

                <DisclosureRow id="due" open={openSection === 'due'} setOpen={setOpenSection} label="Due date"
                  valueHint={(DUE_OPTIONS.find(o => o.v === Number(state.due_days)) || { label: `Net ${state.due_days}` }).label}
                  testid="row-due">
                  <select value={state.due_days} onChange={e => set('due_days', Number(e.target.value))} disabled={isReadOnly} data-testid="due-select"
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-violet-400">
                    {DUE_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
                  </select>
                </DisclosureRow>

                <ToggleRow label="Send reminders" checked={state.reminders_enabled} onChange={v => set('reminders_enabled', v)} testid="toggle-reminders" disabled={isReadOnly} />
                {state.reminders_enabled && (
                  <ReminderSchedule offsets={state.reminder_offsets} dueDays={Number(state.due_days)} dateIssued={state.date_issued} />
                )}
              </SectionGroup>

              {/* RECURRING — Iter 170 — Expanded panel uses a vertical
                  stack with stacked field labels so every input (interval,
                  cycles, AND end date) is visible without horizontal
                  truncation. The section auto-scrolls into view when the
                  toggle is flipped so users never need to scroll. */}
              <SectionGroup title="Recurring" testid="grp-recurring">
                <ToggleRow
                  label="Make this recurring"
                  checked={state.recurring.enabled}
                  onChange={(v) => {
                    set('recurring.enabled', v);
                    if (v) {
                      // Wait for the panel to mount, then scroll it fully
                      // into the sidebar viewport.
                      setTimeout(() => {
                        const el = document.querySelector('[data-testid="recurring-panel"]');
                        el?.scrollIntoView({ behavior: 'smooth', block: 'end' });
                      }, 30);
                    }
                  }}
                  testid="toggle-recurring"
                  disabled={isReadOnly}
                />
                {state.recurring.enabled && (
                  <div className="px-5 pb-4 space-y-2.5" data-testid="recurring-panel">
                    <div>
                      <label className="block text-[11px] uppercase tracking-wider text-white/40 mb-1">Frequency</label>
                      <select value={state.recurring.interval} onChange={e => set('recurring.interval', e.target.value)} disabled={isReadOnly} data-testid="recurring-interval"
                        className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-violet-400">
                        <option value="week">Weekly</option>
                        <option value="month">Monthly</option>
                        <option value="quarter">Quarterly</option>
                        <option value="year">Yearly</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[11px] uppercase tracking-wider text-white/40 mb-1">Number of cycles</label>
                      <input type="number" min={0} value={state.recurring.count} onChange={e => set('recurring.count', e.target.value)} placeholder="0 = forever" disabled={isReadOnly} data-testid="recurring-count"
                        className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-violet-400" />
                    </div>
                    <div>
                      <label className="block text-[11px] uppercase tracking-wider text-white/40 mb-1">End date (optional)</label>
                      <input type="date" value={state.recurring.end_date || ''} onChange={e => set('recurring.end_date', e.target.value)} disabled={isReadOnly} data-testid="recurring-end-date"
                        className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-violet-400" />
                      <p className="text-[10.5px] text-white/40 mt-1">Series stops when cycles run out OR this date is reached — whichever comes first.</p>
                    </div>
                  </div>
                )}
              </SectionGroup>
            </div>
          )}
        </aside>
      </div>

      <NewClientModal
        open={showNewClient}
        onClose={() => setShowNewClient(false)}
        onCreated={(c) => setState(s => ({ ...s, client_id: c.id, client: clientFromCRM(c) }))}
        prefill={state.client}
      />
    </div>
  );
}

/* ═════════════════════════ Squarespace-style preview ═════════════════════════ */
function SquarespacePreview({ state, totals, status, brand = {} }) {
  const dueLabel = (DUE_OPTIONS.find(o => o.v === Number(state.due_days)) || { label: `Net ${state.due_days}` }).label;
  // Iter 170 — Show the person's name and the business as separate stacked
  // lines so the user can fill both without one overwriting the other.
  const personName = (state.client.name || '').trim();
  const bizName = (state.client.business_name || '').trim();
  return (
    <div className="bg-white rounded-2xl shadow-[0_30px_60px_-20px_rgba(0,0,0,0.35)] p-10 sm:p-14 text-gray-800 printable-invoice" data-testid="invoice-preview">
      {/* Iter 170 — Auto-show the business logo (from /api/settings) at the
          top of the invoice. Falls back to brand name text, then to a
          generic title so the layout never collapses. */}
      <div className="text-center mb-12" data-testid="invoice-brand-header">
        {brand.logo_url ? (
          <img
            src={brand.logo_url}
            alt={brand.brand_name || 'Business logo'}
            className="mx-auto max-h-16 object-contain mb-2"
            data-testid="invoice-brand-logo"
          />
        ) : null}
        <div className="text-gray-700 text-sm font-medium" data-testid="invoice-brand-name">
          {brand.brand_name || 'Your Site Title'}
        </div>
      </div>

      {/* Invoice meta */}
      <div className="text-center mb-12">
        <div className="text-gray-700 text-sm">Invoice #{state.number || generatedNumber(state)}</div>
        <div className="text-gray-500 text-xs mt-1">Date Issued: {fmtDateNice(state.date_issued)}</div>
        {state.header_message && state.header_message.replace(/<[^>]+>/g, '').trim() && (
          <div
            className="italic text-gray-600 text-sm mt-6"
            data-testid="invoice-header-message"
            dangerouslySetInnerHTML={{ __html: state.header_message }}
          />
        )}
      </div>

      {/* Amount card */}
      <div className="rounded-xl border border-gray-200 p-8 mb-12">
        <div className="text-gray-600 text-base mb-2">{state.title || 'Invoice'}</div>
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="text-4xl font-bold text-gray-900 tabular-nums">{money(totals.total)}</div>
          <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs whitespace-nowrap">{dueLabel}</span>
        </div>
        <button type="button" disabled
          className="w-full py-4 rounded-md bg-black text-white text-xs font-semibold tracking-[0.22em] uppercase cursor-default">
          {status === 'paid' ? 'Paid' : status === 'void' || status === 'voided' ? 'Voided' : 'Pay invoice'}
        </button>
      </div>

      {/* Billed to — Iter 170 — Render personal name and business name as
          independent stacked lines (business under the person) so neither
          replaces the other in the preview/print output. */}
      <div className="mb-10">
        <div className="font-semibold text-gray-900 mb-1">Billed to</div>
        {personName && (
          <div className="text-sm text-gray-900" data-testid="billed-to-name">{personName}</div>
        )}
        {bizName && (
          <div className={`text-sm ${personName ? 'text-gray-600' : 'text-gray-900'}`} data-testid="billed-to-business">
            {bizName}
          </div>
        )}
        {!personName && !bizName && <div className="text-sm text-gray-400">—</div>}
        {state.client.email && <div className="text-xs text-gray-500 mt-0.5">{state.client.email}</div>}
        {state.client.billing_address && (
          <div className="text-xs text-gray-500 whitespace-pre-line mt-0.5">{state.client.billing_address}</div>
        )}
      </div>

      <hr className="border-gray-200 mb-8" />

      {/* Order summary */}
      <div>
        <div className="text-2xl text-gray-900 font-light mb-6">Order summary</div>
        <div className="space-y-5">
          {state.line_items.map(li => {
            const gross = (Number(li.quantity) || 0) * Number(li.unit_price || 0);
            const amt = gross * (1 - (Number(li.discount_pct) || 0) / 100);
            const hasContent = li.description || Number(li.unit_price) > 0;
            return (
              <div key={li.id} className="flex justify-between items-start text-sm">
                <div>
                  <div className="text-gray-900">{li.description || <span className="text-gray-400 italic">Example item</span>}</div>
                  <div className="text-xs text-gray-500 mt-0.5">Qty: {li.quantity}</div>
                  <div className="text-xs text-gray-500">${Number(li.unit_price || 0).toFixed(2)} / item</div>
                </div>
                <div className="text-gray-900 tabular-nums">{hasContent ? `$${amt.toFixed(2)}` : '$0.00'}</div>
              </div>
            );
          })}
        </div>

        {state.custom_fields?.length > 0 && (
          <div className="mt-8 pt-6 border-t border-gray-100 grid grid-cols-2 gap-3">
            {state.custom_fields.map(f => (
              <div key={f.id}>
                <div className="text-[10px] uppercase tracking-wider text-gray-400">{f.label || 'Field'}</div>
                <div className="text-xs text-gray-700">{f.value || '—'}</div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 pt-5 border-t border-gray-200 space-y-1.5 text-sm">
          <RowLine label="Subtotal" value={money(totals.subtotal)} />
          {totals.invDiscount > 0 && <RowLine label="Discount" value={`-${money(totals.invDiscount)}`} dim />}
          {totals.tax > 0       && <RowLine label={`Tax (${state.tax_rate}%)`} value={money(totals.tax)} dim />}
          {totals.shipping > 0  && <RowLine label="Shipping" value={money(totals.shipping)} dim />}
          <div className="pt-3 border-t border-gray-200 flex justify-between text-base font-bold text-gray-900">
            <span>Total</span><span className="tabular-nums">{money(totals.total)}</span>
          </div>
        </div>
      </div>

      {/* Iter 170 — Footer note (longer memo). Renders only when set, so
          single-message users (header_message only) get a clean look. */}
      {state.memo && state.memo.replace(/<[^>]+>/g, '').trim() && (
        <div
          className="mt-10 pt-6 border-t border-gray-200 text-sm text-gray-600 prose prose-sm max-w-none"
          data-testid="invoice-footer-memo"
          dangerouslySetInnerHTML={{ __html: state.memo }}
        />
      )}
    </div>
  );
}
const RowLine = ({ label, value, dim }) => (
  <div className={`flex justify-between ${dim ? 'text-gray-500' : 'text-gray-700'}`}><span>{label}</span><span className="tabular-nums">{value}</span></div>
);
const generatedNumber = (state) => {
  // Deterministic 8-char preview number based on date + seed
  const seed = (state.title || '') + (state.date_issued || '');
  let h = 0; for (let i = 0; i < seed.length; i++) { h = (h * 31 + seed.charCodeAt(i)) & 0xffffffff; }
  return Math.abs(h).toString(36).slice(0, 8).toUpperCase();
};

/* ═════════════════════════ Section primitives (Squarespace flat rows) ═════════════════════════ */
const Tab = ({ id, active, setActive, children }) => (
  <button onClick={() => setActive(id)} data-testid={`tab-${id}`}
    className={`relative pb-2.5 text-sm transition-colors ${active === id ? 'text-white' : 'text-white/40 hover:text-white/70'}`}>
    {children}
    {active === id && <span className="absolute left-0 right-0 -bottom-[14px] h-0.5 bg-white rounded-full" />}
  </button>
);

const SectionGroup = ({ title, children, testid }) => (
  <div className="py-3" data-testid={testid}>
    <div className="px-5 mb-1 text-[11px] uppercase tracking-[0.16em] text-white/40">{title}</div>
    {children}
  </div>
);

function DisclosureRow({ id, open, setOpen, label, valueHint, children, testid }) {
  const toggle = () => setOpen(open ? null : id);
  return (
    <div data-testid={testid}>
      <button onClick={toggle} className="w-full px-5 py-3 flex items-center justify-between text-left hover:bg-white/[0.025] transition-colors">
        <span className="text-sm text-white">{label}</span>
        <span className="flex items-center gap-2 text-xs text-white/40 max-w-[55%]">
          {valueHint && <span className="truncate">{valueHint}</span>}
          {open ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronRight className="w-4 h-4 text-white/40" />}
        </span>
      </button>
      {open && <div className="px-5 pb-4 -mt-1">{children}</div>}
    </div>
  );
}

function ToggleRow({ label, checked, onChange, testid, disabled }) {
  return (
    <div className="px-5 py-3 flex items-center justify-between" data-testid={testid + '-row'}>
      <span className="text-sm text-white">{label}</span>
      <button role="switch" aria-checked={checked} disabled={disabled} onClick={() => onChange(!checked)} data-testid={testid}
        className={`relative w-10 h-5 rounded-full transition-colors disabled:opacity-30 ${checked ? 'bg-emerald-500' : 'bg-white/20'}`}>
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${checked ? 'translate-x-5' : ''}`} />
      </button>
    </div>
  );
}

const FieldStacked = ({ label, v, on, testid, type = 'text', placeholder, disabled }) => (
  <div>
    <div className="text-[11px] text-white/40 mb-1">{label}</div>
    <input type={type} value={v ?? ''} onChange={e => on(e.target.value)} placeholder={placeholder} data-testid={testid} disabled={disabled}
      className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/25 outline-none focus:border-violet-400 disabled:opacity-50" />
  </div>
);

/* ═════════════════════════ Line item row ═════════════════════════ */
function LineItemRow({ idx, li, onUpdate, onDup, onDel, onDragStart, onDragOver, onDrop, dragging, disabled }) {
  return (
    <div draggable={!disabled} onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop} data-testid={`line-item-${idx}`}
      className={`rounded-lg border border-white/5 bg-white/[0.02] p-3 ${dragging ? 'opacity-50' : ''} group`}>
      <div className="flex items-center gap-1.5 mb-2">
        <GripVertical className="w-3.5 h-3.5 text-white/20 cursor-grab" />
        <input value={li.description} onChange={e => onUpdate('description', e.target.value)} placeholder="Item description" data-testid={`li-desc-${idx}`} disabled={disabled}
          className="flex-1 px-2 py-1.5 rounded bg-transparent border border-white/10 text-white text-sm outline-none focus:border-violet-400 disabled:opacity-50" />
        <button onClick={onDup} disabled={disabled} className="text-white/40 hover:text-white p-1 opacity-0 group-hover:opacity-100 disabled:opacity-30" data-testid={`li-dup-${idx}`}><Copy className="w-3.5 h-3.5" /></button>
        <button onClick={onDel} disabled={disabled} className="text-rose-300/70 hover:text-rose-200 p-1 opacity-0 group-hover:opacity-100 disabled:opacity-30" data-testid={`li-del-${idx}`}><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        <div>
          <div className="text-[10px] text-white/40 mb-0.5">Qty</div>
          <input type="number" min={1} value={li.quantity} onChange={e => onUpdate('quantity', e.target.value)} disabled={disabled} data-testid={`li-qty-${idx}`}
            className="w-full px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-sm outline-none text-right disabled:opacity-50" />
        </div>
        <div>
          <div className="text-[10px] text-white/40 mb-0.5">Unit $</div>
          <input type="number" step="0.01" value={li.unit_price} onChange={e => onUpdate('unit_price', e.target.value)} disabled={disabled} data-testid={`li-price-${idx}`}
            className="w-full px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-sm outline-none text-right disabled:opacity-50" />
        </div>
        <div>
          <div className="text-[10px] text-white/40 mb-0.5">Disc %</div>
          <input type="number" min={0} max={100} value={li.discount_pct} onChange={e => onUpdate('discount_pct', e.target.value)} disabled={disabled} data-testid={`li-disc-${idx}`}
            className="w-full px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-sm outline-none text-right disabled:opacity-50" />
        </div>
        <div>
          <div className="text-[10px] text-white/40 mb-0.5">Tax</div>
          <label className="flex items-center justify-center px-2 py-1.5 rounded bg-white/5 border border-white/10">
            <input type="checkbox" checked={li.taxable} onChange={e => onUpdate('taxable', e.target.checked)} disabled={disabled} data-testid={`li-tax-${idx}`} />
          </label>
        </div>
      </div>
    </div>
  );
}

/* ═════════════════════════ Client picker & form ═════════════════════════ */
function PickedClient({ state, onClear }) {
  return (
    <div className="px-5 py-3 flex items-center justify-between" data-testid="picked-client-card">
      <div className="min-w-0">
        <div className="text-sm text-white truncate" data-testid="picked-client-name">
          {state.client.name || state.client.business_name || 'Unnamed'}
        </div>
        {state.client.name && state.client.business_name && (
          <div className="text-xs text-white/60 truncate" data-testid="picked-client-business">{state.client.business_name}</div>
        )}
        <div className="text-xs text-white/40 truncate">{state.client.email}</div>
      </div>
      <button onClick={onClear} data-testid="unlink-client" className="text-white/40 hover:text-white p-1" title="Unlink client">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function ClientPicker({ state, setState, onShowNewClient }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounce = useRef(null);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    const q = query.trim();
    if (!q) { setResults([]); return; }
    setSearching(true);
    debounce.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/builder/crm/clients`, { headers: auth(), params: { search: q } });
        setResults((data.items || []).slice(0, 6));
      } catch (e) { /* silent */ }
      finally { setSearching(false); }
    }, 200);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [query]);

  const pick = (c) => {
    setState(s => ({ ...s, client_id: c.id, client: clientFromCRM(c) }));
    setQuery(''); setResults([]);
  };

  return (
    <div className="space-y-2 mb-4 relative" data-testid="client-picker">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search your CRM" data-testid="client-picker-input"
            className="w-full pl-8 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/25 outline-none focus:border-violet-400" />
        </div>
        <button onClick={onShowNewClient} data-testid="open-new-client"
          className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white text-xs inline-flex items-center gap-1.5">
          <UserPlus className="w-3.5 h-3.5" /> New
        </button>
      </div>
      {results.length > 0 && (
        <div className="absolute left-0 right-0 top-12 z-30 rounded-lg bg-[#161635] border border-white/10 shadow-2xl overflow-hidden" data-testid="client-search-results">
          {results.map(c => (
            <button key={c.id} onClick={() => pick(c)} data-testid={`client-pick-${c.id}`}
              className="w-full text-left px-3 py-2 hover:bg-white/5 border-b border-white/5 last:border-0">
              <div className="text-white text-sm">{c.business_name || c.name || 'Unnamed'}</div>
              <div className="text-xs text-white/50">{c.email}</div>
            </button>
          ))}
        </div>
      )}
      {!searching && query && results.length === 0 && (
        <div className="text-xs text-white/40">No matches. <button onClick={onShowNewClient} className="text-violet-300 hover:underline" data-testid="empty-add-new">Add &quot;{query}&quot; as a new client</button></div>
      )}
    </div>
  );
}

function ClientForm({ state, set }) {
  return (
    <div className="grid grid-cols-2 gap-2 pt-2 border-t border-white/5">
      <FieldStacked label="Name"          v={state.client.name}          on={v => set('client.name', v)}          testid="client-name" />
      <FieldStacked label="Business"      v={state.client.business_name} on={v => set('client.business_name', v)} testid="client-business" />
      <FieldStacked label="Email *"       v={state.client.email}         on={v => set('client.email', v)}         testid="client-email" type="email" />
      <FieldStacked label="Phone"         v={state.client.phone}         on={v => set('client.phone', v)}         testid="client-phone" />
      <div className="col-span-2">
        <FieldStacked label="Billing address" v={state.client.billing_address} on={v => set('client.billing_address', v)} testid="client-billing" />
      </div>
    </div>
  );
}

/* ═════════════════════════ Reminder schedule ═════════════════════════ */
function ReminderSchedule({ offsets, dueDays, dateIssued }) {
  const baseDate = useMemo(() => {
    try { const d = new Date(dateIssued); d.setDate(d.getDate() + Number(dueDays || 0)); return d; } catch { return new Date(); }
  }, [dateIssued, dueDays]);
  return (
    <div className="px-5 pb-3 space-y-2" data-testid="reminder-schedule">
      {offsets.map(off => {
        const d = new Date(baseDate); d.setDate(d.getDate() + off);
        return (
          <div key={off} className="flex items-center justify-between py-2 border-t border-white/5" data-testid={`reminder-row-${off}`}>
            <div className="flex items-center gap-2.5">
              <Bell className="w-3.5 h-3.5 text-white/30" />
              <div>
                <div className="text-sm text-white">{REMINDER_LABEL[off] || `${off} Days overdue`}</div>
                <div className="text-[11px] text-white/40">{fmtDateNice(d)}</div>
              </div>
            </div>
            <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-white/5 text-white/50 border border-white/10">Scheduled</span>
          </div>
        );
      })}
    </div>
  );
}

/* ═════════════════════════ Memo editor ═════════════════════════ */
function MemoEditor({ value, onChange, disabled }) {
  const ref = useRef(null);
  const initialValue = useRef(value);
  const exec = (cmd, arg) => { if (disabled) return; ref.current?.focus(); document.execCommand(cmd, false, arg); onChange(ref.current?.innerHTML || ''); };
  useEffect(() => {
    // Initialise the editor's HTML ONCE on mount; afterwards the user's typing
    // owns the DOM, so we don't sync from `value` to avoid cursor jumps.
    if (ref.current) ref.current.innerHTML = initialValue.current || '';
    // We intentionally read initialValue.current and don't depend on `value`.
  }, []);
  return (
    <div>
      <div className="flex gap-1 mb-2">
        <ToolBtn onClick={() => exec('bold')} testid="memo-bold"><Bold className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec('italic')} testid="memo-italic"><Italic className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={() => exec('insertUnorderedList')} testid="memo-list"><ListIcon className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn onClick={async () => { const u = await themedPrompt('Link URL'); if (u) exec('createLink', u); }} testid="memo-link"><LinkIcon className="w-3.5 h-3.5" /></ToolBtn>
      </div>
      <div ref={ref} contentEditable={!disabled} suppressContentEditableWarning data-testid="memo-editor"
        onBlur={() => onChange(ref.current?.innerHTML || '')}
        className={`min-h-[100px] p-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-violet-400 prose prose-invert prose-sm max-w-none ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`} />
    </div>
  );
}
const ToolBtn = ({ onClick, testid, children }) => (
  <button onMouseDown={e => e.preventDefault()} onClick={onClick} data-testid={testid}
    className="w-7 h-7 rounded bg-white/5 hover:bg-white/10 text-white/70 hover:text-white flex items-center justify-center">{children}</button>
);

/* ═════════════════════════ Print styles ═════════════════════════ */
function PrintStyles() {
  return (
    <style>{`
      @media print {
        body { background: white !important; }
        .no-print { display: none !important; }
        .print-only-area { background: white !important; padding: 0 !important; }
        .printable-invoice {
          box-shadow: none !important;
          border-radius: 0 !important;
          max-width: 100% !important;
          padding: 24px !important;
        }
      }
    `}</style>
  );
}

/* ═════════════════════════ misc ═════════════════════════ */
function truncateHtml(html, n) {
  const text = (html || '').replace(/<[^>]+>/g, '').trim();
  if (!text) return '';
  return text.length > n ? text.slice(0, n) + '…' : text;
}
